import { useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useTranslation } from "react-i18next";

export interface HotelManualFaqItem {
  id: string;
  hotel_id: string;
  question_es: string;
  question_en: string | null;
  question_pt: string | null;
  answer_es: string;
  answer_en: string | null;
  answer_pt: string | null;
  category: string;
  display_order: number;
  is_active: boolean;
}

export function useHotelManualFaq(hotelId: string | number | null | undefined) {
  const [faqItems, setFaqItems] = useState<HotelManualFaqItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { i18n } = useTranslation();

  const fetchFaqItems = useCallback(async () => {
    if (!hotelId) {
      setFaqItems([]);
      return;
    }

    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from("hotel_manual_faq")
        .select("*")
        .eq("hotel_id", hotelId.toString())
        .eq("is_active", true)
        .order("display_order", { ascending: true });

      if (error) {
        console.error("Error fetching hotel manual FAQ:", error);
        setFaqItems([]);
      } else {
        setFaqItems(data || []);
      }
    } catch (err) {
      console.error("Error fetching hotel manual FAQ:", err);
      setFaqItems([]);
    } finally {
      setIsLoading(false);
    }
  }, [hotelId]);

  useEffect(() => {
    fetchFaqItems();
  }, [fetchFaqItems]);

  const refetch = useCallback(() => {
    fetchFaqItems();
  }, [fetchFaqItems]);

  // Helper to get question in current language with fallback to Spanish
  const getLocalizedQuestion = useCallback((item: HotelManualFaqItem): string => {
    const lang = i18n.language;
    
    if (lang.startsWith('pt')) {
      return item.question_pt || item.question_es;
    } else if (lang.startsWith('en')) {
      return item.question_en || item.question_es;
    }
    return item.question_es;
  }, [i18n.language]);

  // Helper to get answer in current language with fallback to Spanish
  const getLocalizedAnswer = useCallback((item: HotelManualFaqItem): string => {
    const lang = i18n.language;
    
    if (lang.startsWith('pt')) {
      return item.answer_pt || item.answer_es;
    } else if (lang.startsWith('en')) {
      return item.answer_en || item.answer_es;
    }
    return item.answer_es;
  }, [i18n.language]);

  return { faqItems, isLoading, refetch, getLocalizedQuestion, getLocalizedAnswer };
}
